import type { Article } from '@/mocks/articles';

interface SEOMetaProps {
  type: string;
  data: Record<string, unknown>;
}

function generateArticleSchema(article: Article) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '乐球体育',
      logo: {
        '@type': 'ImageObject',
        url: 'https://aikaimanghe.cn/logo.png',
      },
    },
  };
}

function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '乐球体育',
    url: 'https://aikaimanghe.cn',
    description: '乐球体育是您最值得信赖的专业体育平台，涵盖足球、篮球、综合体育赛事资讯、直播与数据分析。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6688',
      contactType: 'customer service',
      email: 'contact@leqiusports.com',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '体育中心路88号',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };
}

function generateWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '乐球体育',
    url: 'https://aikaimanghe.cn',
    description: '乐球体育是您最值得信赖的专业体育平台，涵盖足球、篮球、综合体育赛事资讯、直播与数据分析。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://aikaimanghe.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

function generateBreadcrumbSchema(items: { label: string; href?: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://aikaimanghe.cn${item.href}` : undefined,
    })),
  };
}

function generateFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '乐球体育提供哪些体育项目的资讯？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '乐球体育提供足球、篮球以及综合体育（包括网球、田径、游泳、乒乓球、羽毛球等）的全方位赛事资讯、直播信息与数据分析。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在乐球体育查看实时比分？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在乐球体育首页的实时比分板块查看正在进行的比赛比分，也可以在各个分类页面中找到对应赛事的实时数据更新。',
        },
      },
      {
        '@type': 'Question',
        name: '乐球体育的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '乐球体育全天候24小时不间断更新体育资讯，重大赛事期间实时推送比分和战报，确保您第一时间获取最新体育动态。',
        },
      },
      {
        '@type': 'Question',
        name: '乐球体育是否提供赛事直播服务？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '乐球体育提供文字直播和实时数据更新服务，部分赛事提供视频直播链接。具体直播安排请关注网站公告和赛事页面。',
        },
      },
    ],
  };
}

export { generateArticleSchema, generateOrganizationSchema, generateWebSiteSchema, generateBreadcrumbSchema, generateFAQSchema };

export default function SEOMeta({ type, data }: SEOMetaProps) {
  let schema = null;

  switch (type) {
    case 'organization':
      schema = generateOrganizationSchema();
      break;
    case 'website':
      schema = generateWebSiteSchema();
      break;
    case 'article':
      schema = generateArticleSchema(data as unknown as Article);
      break;
    case 'breadcrumb':
      schema = generateBreadcrumbSchema(data as unknown as { label: string; href?: string }[]);
      break;
    case 'faq':
      schema = generateFAQSchema();
      break;
    default:
      break;
  }

  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}